import type React from 'react';
import { useEffect, useMemo } from 'react';

import { useDutchieIntelligenceContext } from '../context/dutchie-intelligence';

import type { ContextMetadata } from '../context/types';

/**
 * Generic hook for registering context with the Dutchie Intelligence service.
 * Handles memoization and registration/cleanup automatically.
 *
 * This hook can be used across all resource bridges to simplify the pattern of:
 * 1. Building context data from dependencies
 * 2. Memoizing the stringified result
 * 3. Registering/unregistering with the intelligence context
 *
 * @template T - The type of context data returned by buildContext. This provides:
 *   - Input validation for the buildContext function
 *   - Type checking inside the hook (ensures the data can be stringified)
 *   - Better IntelliSense and autocomplete while writing builder functions
 *   Note: TypeScript will automatically infer T from buildContext's return type,
 *   so you typically don't need to specify it explicitly. The generic does NOT
 *   affect the return type of this hook (because it does not return anything).
 *
 * @param contextId - Unique identifier for this context
 * @param buildContext - Function that builds the context data (returns null to skip registration)
 * @param dependencies - Dependencies array for memoization
 * @param scope - Context scope (e.g., 'app', 'products')
 * @param metadata - Context metadata (priority, type, source, etc.)
 *
 * @example
 * ```tsx
 * // TypeScript infers T = UserContext from buildUserContext's return type
 * useContextRegistration(
 *   'user-profile',
 *   () => buildUserContext(user),
 *   [user],
 *   'app',
 *   { type: 'user-data', priority: 'high', source: 'recoil', global: true }
 * );
 * ```
 */
export function useContextRegistration<T>(
  contextId: string,
  buildContext: () => T | null,
  dependencies: React.DependencyList,
  scope: string,
  metadata: Partial<ContextMetadata>
): void {
  const { setComponentContext, removeComponentContext } = useDutchieIntelligenceContext();

  // Memoize the stringified context - only recompute when dependencies change
  const contextString = useMemo(() => {
    const context = buildContext();
    return context ? JSON.stringify(context) : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  // Register or remove context based on whether we have data
  useEffect(() => {
    if (!contextString) {
      removeComponentContext(contextId);
      return undefined;
    }

    setComponentContext(contextId, contextString, scope, metadata);

    return () => {
      removeComponentContext(contextId);
    };
  }, [contextString, contextId, scope, metadata, setComponentContext, removeComponentContext]);
}
